import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import MapPoolSelector from './MapPoolSelector';
import VetoSimulator from './VetoSimulator';
import usePermissions from '../../hooks/usePermissions';
import './VetoFlow.css';

/**
 * Interactive demo page — simulates the full veto flow locally.
 * No backend needed (beyond public `/api/web/maps` + `/api/maps/stats`).
 * Access via /veto-demo. Two steps: pool selection (exercises the real
 * MapPoolSelector, including sort modes) then ban/pick simulation via
 * the shared VetoSimulator component.
 */

const FALLBACK_POOL = [
  'de_mirage', 'de_inferno', 'de_nuke', 'de_overpass',
  'de_ancient', 'de_anubis', 'de_vertigo',
];

/**
 * Standalone veto demo screen — owner picks match-type / mode / map pool,
 * then VetoSimulator runs the full veto sequence in-memory. Used to
 * iterate on veto UI changes without creating a real match. Gated by
 * SHOW_VETO_DEMO feature flag.
 */
export default function VetoDemo() {
  const { t } = useTranslation();
  const [step, setStep] = useState('pool');
  const [matchType, setMatchType] = useState('BO3');
  const [vetoMode, setVetoMode] = useState('team_vote');
  const [selectedPool, setSelectedPool] = useState(FALLBACK_POOL);
  const [knifeRound, setKnifeRound] = useState(true);
  const [resetKey, setResetKey] = useState(0);
  const { features } = usePermissions();

  const handlePoolStarted = useCallback((config) => {
    setMatchType(config.match_type || 'BO3');
    setVetoMode(config.veto_mode || 'team_vote');
    setSelectedPool(config.pool || FALLBACK_POOL);
    setKnifeRound(!!config.knife_round);
    setResetKey(k => k + 1);
    setStep('veto');
  }, []);

  const changeMatchType = (type) => {
    setMatchType(type);
    setStep('pool');
  };

  if (step === 'pool') {
    return (
      <div className="veto-flow" style={{ maxWidth: 900, margin: '2rem auto', padding: '1rem' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>{t('veto.demoTitle')}</h1>
        <p style={{ textAlign: 'center', color: '#888', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
          {t('veto.demoSubtitle')}
        </p>

        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <span style={{ color: '#888', fontSize: '0.8rem', marginRight: '0.3rem' }}>{t('veto.demoMatchType')}</span>
          {['BO1', 'BO3', 'BO5'].map(mt => (
            <button
              key={mt}
              onClick={() => setMatchType(mt)}
              style={{
                padding: '0.35rem 0.7rem',
                fontSize: '0.8rem',
                borderRadius: 6,
                border: matchType === mt ? '1px solid #4ade80' : '1px solid rgba(255,255,255,0.2)',
                background: matchType === mt ? 'rgba(74,222,128,0.2)' : 'transparent',
                color: matchType === mt ? '#4ade80' : '#aaa',
                cursor: 'pointer',
              }}
            >
              {mt}
            </button>
          ))}
        </div>

        <MapPoolSelector
          match={{ id: 'demo', type: matchType, team1: { players: [1,2,3,4,5] }, team2: { players: [6,7,8,9,10] } }}
          gameMode="competitive"
          demoMode
          onVetoStarted={handlePoolStarted}
        />
      </div>
    );
  }

  return (
    <div className="veto-flow" style={{ maxWidth: 800, margin: '2rem auto', padding: '1rem' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>{t('veto.demoTitle')}</h1>
      <p style={{ textAlign: 'center', color: '#888', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
        {t('veto.demoSubtitle')}
      </p>

      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.8rem', marginRight: '0.3rem' }}>{t('veto.demoMatchType')}</span>
          {['BO1', 'BO3', 'BO5'].map(mt => (
            <button
              key={mt}
              onClick={() => changeMatchType(mt)}
              style={{
                padding: '0.35rem 0.7rem',
                fontSize: '0.8rem',
                borderRadius: 6,
                border: matchType === mt ? '1px solid #4ade80' : '1px solid rgba(255,255,255,0.2)',
                background: matchType === mt ? 'rgba(74,222,128,0.2)' : 'transparent',
                color: matchType === mt ? '#4ade80' : '#aaa',
                cursor: 'pointer',
              }}
            >
              {mt}
            </button>
          ))}
        </div>
        <button
          onClick={() => setStep('pool')}
          style={{
            padding: '0.35rem 0.7rem',
            fontSize: '0.8rem',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'transparent',
            color: '#aaa',
            cursor: 'pointer',
          }}
        >
          {t('veto.demoChangePool')}
        </button>
        <button
          onClick={() => setResetKey(k => k + 1)}
          style={{
            padding: '0.35rem 0.7rem',
            fontSize: '0.8rem',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'transparent',
            color: '#aaa',
            cursor: 'pointer',
          }}
        >
          {t('veto.demoRestart')}
        </button>
      </div>

      <VetoSimulator
        matchType={matchType}
        vetoMode={vetoMode}
        pool={selectedPool}
        knifeRound={knifeRound}
        resetKey={resetKey}
        bo1BanFinish={!!features?.BO1_BAN_FINISH}
      />
    </div>
  );
}
